import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { FloatLabelType } from '@angular/material/form-field';
import { MatSelectChange } from '@angular/material/select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MunilyFilterFieldSize } from '../filter-fields/filter-field.types';
import {
  MunilySelectFieldOption,
  MunilyTextFieldErrorMessages,
  MunilyTextFieldWidthLayout,
} from '../select-field/select-field.types';
import { MUNILY_FILTER_SELECT_FIELD_DEFAULTS } from './filter-select-field.types';

/**
 * Select para **filtros** (toolbar). Siempre `appearance="outline"`.
 * Opcional: limpiar selección (`clearable`) con icono y fondo activo (DS / Figma 5176-6553).
 */
@Component({
  selector: 'app-filter-select-field',
  templateUrl: './filter-select-field.component.html',
  styleUrls: ['./filter-select-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterSelectFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control?: FormControl;

  @Input() size: MunilyFilterFieldSize = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.size;
  @Input() widthLayout: MunilyTextFieldWidthLayout = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.widthLayout;
  @Input() maxWidth?: string;

  @Input() options: MunilySelectFieldOption[] = [];
  @Input() multiple = false;

  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() iconLeading?: string;

  /** Clase del overlay; por defecto estilos solo para panel de filtro (no `custom-select-panel`). */
  @Input() panelClass = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.panelClass;

  @Input() disableOptionCentering = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.disableOptionCentering;
  @Input() compareWith?: (a: unknown, b: unknown) => boolean;

  /** Muestra icono para vaciar la selección (single). Usar con `clearValue` si el “vacío” no es `null`. */
  @Input() clearable = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.clearable;

  /** Valor al limpiar (p. ej. `''` si la primera opción es “Todos”). Si no se define, se usa `null`. */
  @Input() clearValue?: unknown;

  @Input() clearAriaLabel = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.clearAriaLabel;

  @Input() floatLabel: FloatLabelType = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.floatLabel;
  @Input() disabled = false;
  @Input() successful = false;

  @Input() errorMessage = '';
  @Input() errorMessages: MunilyTextFieldErrorMessages = {};

  @Output() selectionChange = new EventEmitter<MatSelectChange>();
  @Output() valueChange = new EventEmitter<unknown>();
  /** Emite cuando el usuario limpia con el icono (valor resultante = `clearValue` o `null`). */
  @Output() cleared = new EventEmitter<void>();

  @HostBinding('class.munily-ff-host--ds-default')
  get hostDsDefaultMaxWidth(): boolean {
    return this.widthLayout === 'ds' && !this.maxWidth;
  }

  @HostBinding('style.max-width')
  get hostMaxWidthStyle(): string | null {
    if (this.widthLayout === 'fill') {
      return 'none';
    }
    if (this.widthLayout === 'ds' && this.maxWidth) {
      return this.maxWidth;
    }
    return null;
  }

  readonly fallbackControl = new FormControl(null);
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get resolvedControl(): FormControl {
    return this.control ?? this.fallbackControl;
  }

  get resolvedErrorMessage(): string {
    if (this.errorMessage) return this.errorMessage;
    const errors = this.resolvedControl.errors;
    if (!errors) return '';
    return this.firstErrorMessage(errors);
  }

  ngOnInit(): void {
    this.syncDisabled();
    this.wireControlUpdates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.syncDisabled();
    }
    if (changes['control']) {
      this.destroy$.next();
      this.syncDisabled();
      this.wireControlUpdates();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showError(): boolean {
    const c = this.resolvedControl;
    return c.invalid && (c.touched || c.dirty);
  }

  showSuccessOutline(): boolean {
    return this.successful && !this.showError();
  }

  onSelectionChange(ev: MatSelectChange): void {
    this.selectionChange.emit(ev);
    this.valueChange.emit(ev.value);
  }

  onOpenedChange(open: boolean): void {
    if (!open) {
      this.resolvedControl.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  /** Hay valor distinto del estado “vacío” y se puede mostrar el icono de limpiar. */
  showClearButton(): boolean {
    if (!this.clearable || this.disabled || this.multiple) {
      return false;
    }
    const current = this.resolvedControl.value;
    const empty = this.clearValue !== undefined ? this.clearValue : null;
    if (this.compareWith) {
      try {
        if (this.compareWith(current, empty)) {
          return false;
        }
      } catch {
        // continuar con comparación por ===
      }
    }
    if (current === empty) {
      return false;
    }
    if (empty === null && (current === null || current === undefined)) {
      return false;
    }
    return true;
  }

  onClear(): void {
    const reset = this.clearValue !== undefined ? this.clearValue : null;
    this.resolvedControl.setValue(reset);
    this.resolvedControl.markAsDirty();
    this.valueChange.emit(reset);
    this.cleared.emit();
    this.cdr.markForCheck();
  }

  onClearPointerDown(event: MouseEvent): void {
    event.stopPropagation();
  }

  private syncDisabled(): void {
    const ctrl = this.resolvedControl;
    if (this.disabled && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  private wireControlUpdates(): void {
    const ctrl = this.resolvedControl;
    ctrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
    ctrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
  }

  private firstErrorMessage(errors: ValidationErrors): string {
    const key = Object.keys(errors)[0];
    const custom = this.errorMessages[key];
    if (custom) return custom;

    const err = errors[key];
    if (key === 'required') return 'Este campo es obligatorio';
    if (typeof err === 'object' && err?.message) return String(err.message);
    return 'Valor no válido';
  }
}
